<template>
  <div class="employee-block">
    <div class="edit-row">
      <button class="button button--light" @click="isEditing = !isEditing">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
          class="edit-icon">
          <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
          <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"></path>
        </svg>
        {{ isEditing ? 'Cancel' : 'Edit Details' }}
      </button>
    </div>

    <driver-form v-if="isEditing" :driver="employeeDriver" @save="onSave" />

    <div v-else>
      <div class="header-row">
        <div>
          <h2 class="employee-name">{{ profile.forename }} {{ profile.surname }}</h2>
          <div class="employee-email">{{ profile.email }}</div>
        </div>
        <span v-if="profile.mainDriver" class="main-driver-pill">Main Driver</span>
      </div>
      <div class="details-grid">
        <div class="details-section">
          <div>
            <h3 class="detail-label">Address</h3>
            <div class="detail-value">
              <div>{{ profile.homeAddress }}</div>
              <div>{{ profile.postcode || 'Not yet set' }}</div>
            </div>
          </div>
        </div>
        <div class="details-columns">
          <div class="details-section">
            <div>
              <h3 class="detail-label">Mobile Phone</h3>
              <div class="detail-value">{{ profile.mobile || 'Not yet set' }}</div>
            </div>
            <div>
              <h3 class="detail-label">Date of Birth</h3>
              <div class="detail-value">{{ profile.dob ? formattedDob : 'Not yet set' }}</div>
            </div>
            <div>
              <h3 class="detail-label">Driving License</h3>
              <div class="detail-value">{{ profile.driverLicence || 'Not yet set' }}</div>
            </div>
          </div>
          <div class="details-section">
            <div>
              <h3 class="detail-label">Tax Band</h3>
              <div class="detail-value">{{ profile.incomeTaxBand || 'Not yet set' }}%</div>
            </div>
            <div>
              <h3 class="detail-label">UK Resident</h3>
              <div class="detail-value">{{ profile.insuranceQuestions?.['Is the driver a permanent UK resident'] || 'Not yet set' }}</div>
            </div>
            <div>
              <h3 class="detail-label">Full License</h3>
              <div class="detail-value">{{ profile.insuranceQuestions?.['Does the driver hold a full hold a full licence from the UK, EU, EEA, or one of the countries listed within the Insurance Eligibility Criteria document'] || 'Not yet set' }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { useRoute } from 'vue-router';
import { EventBus } from '@/event-bus';
import DriverForm from '@/components/your-profile/salsac/drivers/driver-form.vue';
import formatter from '@/helpers/formatter';

const { proxy } = getCurrentInstance();
const route = useRoute();

const props = defineProps({
  profile: {
    type: Object,
    required: true,
  },
  product: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['employee-saved', 'percent-updated']);

const isEditing = ref(false);

const employeeDriver = computed(() => ({
  driverID: props.profile.driverID,
  firstname: props.profile.forename,
  surname: props.profile.surname,
  email: props.profile.email,
  dob: props.profile.dob,
  address: props.profile.homeAddress,
  postcode: props.profile.postcode,
  mobilePhone: props.profile.mobile,
  mainDriver: props.profile.mainDriver,
  drivingLicense: props.profile.driverLicence,
  taxBand: props.profile.incomeTaxBand ? String(props.profile.incomeTaxBand) : '',
  insuranceQuestions: props.profile.insuranceQuestions || {},
}));

// Percentage: 9 basic fields + 6 insurance questions = 15
const INSURANCE_QUESTION_COUNT = 6;
const isFilled = v => v !== null && v !== undefined && v !== '' && v !== false;

const employeePercent = computed(() => {
  const d = employeeDriver.value;
  const fields = [d.firstname, d.surname, d.email, d.dob, d.address, d.postcode, d.mobilePhone, d.drivingLicense, d.taxBand];
  let filled = fields.filter(isFilled).length;
  filled += Object.values(d.insuranceQuestions).filter(isFilled).length;
  return Math.round((filled / (fields.length + INSURANCE_QUESTION_COUNT)) * 100);
});

watch(employeePercent, val => emit('percent-updated', val), { immediate: true });

const formattedDob = computed(() => formatter.shortDate(props.profile.dob) || 'Not yet set');

function onSave(submitPayload) {
  EventBus.$emit('loading', true);
  EventBus.$emit('loadingTitle', 'Updating Driver Profile');

  proxy.$http.post('/api/your-profile', {
    ...submitPayload,
    scheme: route.params.scheme,
  })
    .then(response => {
      EventBus.$emit('loading', false);
      proxy.$notify({ group: 'notify', type: 'success', title: 'Success', text: 'Driver profile updated successfully' });
      emit('employee-saved', response.data);
      isEditing.value = false;
    })
    .catch(err => {
      EventBus.$emit('loading', false);
      proxy.$notify({ group: 'notify', type: 'error', title: 'Error', text: err.response?.data?.error?.message || 'Failed to update profile' });
    });
}
</script>

<style scoped>
.employee-block {
}

.edit-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 1.5rem;
}

.edit-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 5px;
  font-size: 0.875rem;
  text-transform: uppercase;
}

.edit-icon {
  width: 1rem;
  height: 1rem;
}

.header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.employee-name {
  font-size: 1.5rem;
  font-weight: 500;
  color: var(--color-foreground, #1a1a1a);
}

.employee-email {
  color: var(--color-muted-foreground, #6b7280);
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 4rem;
  row-gap: 1.5rem;
  margin-top: 2rem;
}

.details-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 2rem;
}

.details-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.detail-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-muted-foreground, #6b7280);
  margin-bottom: 0.25rem;
}

.detail-value {
  color: var(--color-foreground, #1a1a1a);
  margin-bottom: 3px;
}

.span-2-cols {
  grid-column: span 2;
}

@media (max-width: 768px) {
  .details-grid {
    grid-template-columns: 1fr;
  }
}
</style>
